// src/components/pageBuilder/components/EventsGrid.tsx

"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import SafeHtml from "@/components/ui/SafeHtml";
import { fetchEventsGrid } from "@/lib/fetchEventsGrid";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import type { EventProduct } from "./FeaturedEvents";
import {
  getEventStart,
  getEventDateLabel,
  isUpcoming,
} from "@/lib/eventDateHelpers";

export type EventsGridFields = {
  hide?: boolean;
  id?: string;
  title?: string;
  category?: number | string | false | null;
};

type EventProductWithExcerpt = EventProduct & {
  excerpt?: { rendered?: string };
  content?: { rendered?: string };
  price_amount?: number | null;
  is_free?: boolean;
  stock_status?: string;
  tickets_left?: number | null;
};

type RibbonSpec = { label: string; modifier?: string } | null;

const DEFAULT_COUNT = 12;

const RIBBON_LABELS = {
  free: "Gratuito",
  soldOut: "Esgotado",
  past: "Concluído",
};

function resolveLocale(lang: string): string {
  if (lang === "pt") return "pt-PT";
  if (lang === "es") return "es-ES";
  return lang || "pt-PT";
}

function getProductTitle(p: EventProduct): string {
  const raw = p.title?.rendered || p.post_title || "";
  return raw
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/&#(\d+);/g, (_, c) => String.fromCharCode(Number(c)))
    .replace(/&amp;/g, "&")
    .replace(/&ndash;/g, "–")
    .replace(/&#8211;/g, "–");
}

function getImageSrc(p: EventProduct): string | null {
  const media = p._embedded?.["wp:featuredmedia"]?.[0];
  if (media) {
    const raw =
      media.media_details?.sizes?.large?.source_url ||
      media.media_details?.sizes?.medium_large?.source_url ||
      media.media_details?.sizes?.medium?.source_url ||
      media.source_url;
    return raw ? toAbsoluteUrl(raw) ?? null : null;
  }
  return p.featured_image ? toAbsoluteUrl(p.featured_image) ?? null : null;
}

function getHref(p: EventProduct): string {
  if (typeof p.link === "string" && p.link) {
    try {
      return new URL(p.link, "https://example.local").pathname;
    } catch { /* fall */ }
  }
  if (typeof p.slug === "string" && p.slug) return p.slug;
  return "#";
}

function stripHtml(s = ""): string {
  return s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function truncateWords(text: string, limit = 20): string {
  const words = text.trim().split(/\s+/);
  if (words.length <= limit) return text;
  return words.slice(0, limit).join(" ") + "…";
}

function getExcerptLike(p: EventProductWithExcerpt): { html?: string; generated?: string } {
  const excerptHtml = (p.excerpt?.rendered || "").trim();
  if (excerptHtml) return { html: excerptHtml };
  const text = stripHtml((p.content?.rendered || "").trim());
  if (!text) return {};
  return { generated: truncateWords(text, 20) };
}

function formatPrice(amount: number): string {
  try {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `€${amount}`;
  }
}

function getRibbonSpec(p: EventProductWithExcerpt, hasEventDate: boolean): RibbonSpec {
  if (hasEventDate && !isUpcoming(p)) {
    return { label: RIBBON_LABELS.past, modifier: "price-ribbon--past" };
  }
  const soldOut =
    p.stock_status === "outofstock" ||
    (typeof p.tickets_left === "number" && p.tickets_left <= 0);
  if (soldOut) {
    return { label: RIBBON_LABELS.soldOut, modifier: "price-ribbon--sold-out" };
  }
  if (p.is_free === true) {
    return { label: RIBBON_LABELS.free };
  }
  const amount = typeof p.price_amount === "number" ? p.price_amount : NaN;
  if (!isNaN(amount) && amount > 0) {
    return { label: formatPrice(amount) };
  }
  return null;
}

function EventGridCard({ product, lang }: { product: EventProductWithExcerpt; lang: string }) {
  const href = getHref(product);
  const title = getProductTitle(product);
  const imgSrc = getImageSrc(product);
  const imgAlt = product._embedded?.["wp:featuredmedia"]?.[0]?.alt_text || title;
  const locale = resolveLocale(lang);
  const dateLabel = getEventDateLabel(
    product,
    locale,
    { day: "2-digit", month: "long", year: "numeric" },
    { compact: true },
  );
  const { html: excerptHtml, generated } = getExcerptLike(product);
  const ribbon = getRibbonSpec(product, !!dateLabel);

  return (
    <Link href={href} className="group block w-full no-underline hover:no-underline">
      {imgSrc ? (
        <div className="relative w-full aspect-[3/2] overflow-hidden">
          {ribbon && (
            <div className={`price-ribbon price-ribbon--sm${ribbon.modifier ? " " + ribbon.modifier : ""}`}>
              <span className="price-ribbon__label">{ribbon.label}</span>
            </div>
          )}
          <Image
            src={imgSrc}
            alt={imgAlt}
            fill
            className="object-cover transition-transform duration-200 ease-out group-hover:scale-[1.025]"
            sizes="(min-width: 768px) 33vw, 100vw"
            priority={false}
          />
        </div>
      ) : null}
      <div className="mt-3">
        {dateLabel ? <div className="font-regular">{dateLabel}</div> : null}
        {title ? (
          <h3 className="mt-1 text-title-4">
            <SafeHtml content={title} />
          </h3>
        ) : null}
        {excerptHtml ? (
          <div className="mt-2 leading-6 text-gray-900 line-clamp-2 [&_p]:m-0 [&_*]:m-0">
            <SafeHtml content={excerptHtml} />
          </div>
        ) : generated ? (
          <div className="mt-2 leading-6 line-clamp-2 text-gray-900">
            <SafeHtml content={generated} />
          </div>
        ) : null}
      </div>
    </Link>
  );
}

export default function EventsGrid({
  data,
  lang,
}: {
  data: EventsGridFields;
  lang?: string;
}) {
  const isHidden = !data || !!data.hide;
  const langCode = lang || "";

  const [events, setEvents] = React.useState<EventProductWithExcerpt[]>([]);

  React.useEffect(() => {
    if (isHidden) return;

    let alive = true;

    fetchEventsGrid(langCode, DEFAULT_COUNT * 3, data.category || null).then(({ products }) => {
      if (!alive) return;
      const upcoming = (products as EventProductWithExcerpt[])
        .filter(isUpcoming)
        .sort((a, b) => {
          const da = getEventStart(a)?.getTime() ?? Infinity;
          const db = getEventStart(b)?.getTime() ?? Infinity;
          return da - db;
        })
        .slice(0, DEFAULT_COUNT);
      setEvents(upcoming);
    });

    return () => {
      alive = false;
    };
  }, [langCode, isHidden, data.category]);

  if (isHidden) return null;
  if (events.length === 0) return null;

  return (
    <div id={data.id}>
      {data.title ? (
        <h2 className="text-title-2">
          <SafeHtml content={data.title} />
        </h2>
      ) : null}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8">
        {events.map((p) => (
          <EventGridCard
            key={p.id ?? p.ID ?? p.slug}
            product={p}
            lang={langCode}
          />
        ))}
      </div>
    </div>
  );
}